import { forwardRef, useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

/** ネイティブ `<input type="month">` の置き換え (2026-09-07)。
 *  Safari等でセグメント編集中にBackspaceを押すと不完全な中間表示 (`2025-1-`等) になる
 *  ネイティブ実装のクセがあり (docs/ui-guidelines.md D3が元々禁止していたOSデフォルト部品)、
 *  カレンダー形式 (年をナビゲートし月をグリッドから選ぶ) のカスタムUIに置き換える。
 *  `value`/`onChange`は 'YYYY-MM' (末尾に '-01' 等が付いた 'YYYY-MM-01' もそのまま受け取れる)。 */

const MONTH_LABELS = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

/** exportして単体テスト可能にする (実装レビューHIGH: 純粋関数だが無テストだった)。 */
export const parseYearMonth = (value: string): { year: number; month: number } | null => {
  const m = value.match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  const year = Number.parseInt(m[1]!, 10);
  const month = Number.parseInt(m[2]!, 10);
  if (month < 1 || month > 12) return null;
  return { year, month };
};

type MonthPickerProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  /** 実装レビューMEDIUM (design-expert/design-and-code): 横並びの2フィールド目
   *  (例: TrendsPage/StoreComparisonPageの「期間終了」) では左揃えのポップアップが
   *  画面右端からはみ出しうるため、右揃えを優先させたい場合のヒント。
   *  ⚠️ 375px幅で横並びが縦積みに変わると、狭いボタンを右揃えにした結果ポップアップが
   *  画面左端からはみ出す実機バグが発覚したため、あくまで「優先」に留め、実際の画面幅・
   *  トリガーの位置を毎回測定して自動的にフォールバックする(0-2-3参照)。 */
  align?: 'left' | 'right';
  /** 隣接する<label htmlFor>のテキスト等、文脈を示す語 (例: '開始月')。
   *  ⚠️ <button>はHTML仕様上labelableなため、<label htmlFor>を付けると本来はそちらが
   *  accessible nameを決めてしまい、現在選択中の値 (例: '2026年6月') が読み上げられなくなる。
   *  ここで明示的にaria-labelを組み立てることでlabelより優先させ、両方を読み上げさせる。 */
  'aria-label'?: string;
};

export const MonthPicker = forwardRef<HTMLButtonElement, MonthPickerProps>(function MonthPicker(
  { id, value, onChange, className, disabled, align = 'left', 'aria-label': ariaLabel },
  forwardedRef,
) {
  const parsed = parseYearMonth(value);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => parsed?.year ?? new Date().getFullYear());
  const [resolvedAlign, setResolvedAlign] = useState<'left' | 'right'>(align);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // 実装レビューで発覚した実機バグの是正: `align`は「希望」に過ぎず、実際にトリガーが
  // 画面上のどこにあるかを開く直前に測定して、はみ出す側であれば逆側へ自動フォールバックする
  // (ポップアップ幅は w-64 = 256px 固定)。
  const DROPDOWN_WIDTH = 256;
  const VIEWPORT_MARGIN = 8;
  const toggleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      if (align === 'right') {
        setResolvedAlign(rect.right - DROPDOWN_WIDTH < VIEWPORT_MARGIN ? 'left' : 'right');
      } else {
        setResolvedAlign(rect.left + DROPDOWN_WIDTH > window.innerWidth - VIEWPORT_MARGIN ? 'right' : 'left');
      }
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (open) setViewYear(parsed?.year ?? new Date().getFullYear());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // NodeSwitcher.tsx / ScopePinToggle.tsx と同一のフォーカストラップ・Escape・outside-clickパターン
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = parsed ? `${parsed.year}年${parsed.month}月` : '選択してください';

  const pick = (m: number) => {
    onChange(`${viewYear}-${String(m).padStart(2, '0')}`);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="relative inline-block">
      <button
        ref={(node) => {
          triggerRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        id={id}
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}: ${label}` : label}
        className={`inline-flex min-h-[44px] min-w-[104px] items-center gap-1.5 text-left disabled:opacity-50 disabled:cursor-not-allowed ${className ?? ''}`}
      >
        <Calendar size={14} className="shrink-0 text-neutral-sub" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="月を選択"
          className={`absolute ${resolvedAlign === 'right' ? 'right-0' : 'left-0'} z-[45] mt-1 w-64 rounded border border-neutral-line bg-white p-3 shadow-lg`}
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              aria-label="前の年"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded hover:bg-neutral-panel"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-neutral-text">{viewYear}年</span>
            <button
              type="button"
              onClick={() => setViewYear((y) => y + 1)}
              aria-label="次の年"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded hover:bg-neutral-panel"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {MONTH_LABELS.map((monthLabel, i) => {
              const m = i + 1;
              const isSelected = parsed?.year === viewYear && parsed?.month === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => pick(m)}
                  aria-pressed={isSelected}
                  className={`min-h-[44px] rounded text-sm ${
                    isSelected ? 'bg-brand-red font-semibold text-white' : 'text-neutral-text hover:bg-neutral-panel'
                  }`}
                >
                  {monthLabel}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});
